import React, { createContext, useContext, useState, useEffect } from "react";
import { 
  CalculatorValues, 
  TaxResults, 
  SavedScenario,
  FrequencyType,
  IncomeInputType,
  TaxYearType,
  EmploymentType
} from "@/lib/types";
import { DEFAULT_CALCULATOR_VALUES } from "@/lib/constants";
import { calculateTaxResults } from "@/lib/calculators";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { nanoid } from "nanoid";
import { parseUrlParams } from "@/lib/shareUtils";

interface CalculatorContextType {
  values: CalculatorValues;
  results: TaxResults;
  savedScenarios: SavedScenario[];
  activeScenarioId: string | null;
  hasModified: boolean;
  setValues: (values: Partial<CalculatorValues>) => void;
  setFrequency: (frequency: FrequencyType) => void;
  setEmploymentType: (type: EmploymentType) => void;
  setTaxYear: (year: TaxYearType) => void;
  setIncomeInputType: (type: IncomeInputType) => void;
  setHoursPerWeek: (hours: number) => void;
  saveScenario: (name?: string) => void;
  loadScenario: (id: string) => void;
  deleteScenario: (id: string) => void;
  compareScenarios: (ids: string[]) => void;
  resetToDefaults: () => void;
}

const CalculatorContext = createContext<CalculatorContextType | undefined>(undefined);

export const CalculatorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use localStorage for calculator values with type cast to ensure correct type
  const [storedValues, setStoredValues] = useLocalStorage<CalculatorValues>("tax-calculator-values", DEFAULT_CALCULATOR_VALUES);
  
  // Initialize with shared values from URL if available, otherwise use stored values
  const [values, setValuesState] = useState<CalculatorValues>(() => {
    const urlParams = parseUrlParams();
    return urlParams || { ...DEFAULT_CALCULATOR_VALUES, ...storedValues };
  });

  const [results, setResults] = useState<TaxResults>(() => calculateTaxResults(values));
  const [savedScenarios, setSavedScenarios] = useLocalStorage<SavedScenario[]>("tax-calculator-scenarios", []);
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(null);
  const [hasModified, setHasModified] = useState<boolean>(false);
  
  // Check for URL parameters on initial load
  useEffect(() => {
    const urlParams = parseUrlParams();
    if (urlParams) {
      setValuesState(urlParams);
      // Remove the query parameters from URL after loading
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Recalculate results whenever values change and save to localStorage
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const updatedResults = calculateTaxResults(values);
      setResults((prevResults) => {
        if (JSON.stringify(prevResults) !== JSON.stringify(updatedResults)) {
          // Only update state and save when there are actual changes
          setStoredValues(values);
          return updatedResults;
        }
        return prevResults;
      });
    }, 200);

    return () => clearTimeout(timeoutId); // Cleanup timeout on unmount or values change
  }, [values, setStoredValues]);

  const setValues = (newValues: Partial<CalculatorValues>) => {
    setValuesState((prev) => ({ ...prev, ...newValues }));
    
    // Set hasModified to true when user makes changes
    setHasModified(true);
    
    // Clear active scenario when making changes
    if (activeScenarioId) {
      setActiveScenarioId(null);
    }
  };

  const setFrequency = (frequency: FrequencyType) => {
    setValues({ frequency });
  };

  const setEmploymentType = (employmentType: EmploymentType) => {
    setValues({ employmentType });
  };
  
  const setTaxYear = (taxYear: TaxYearType) => {
    setValues({ taxYear });
  };
  
  const setIncomeInputType = (incomeInputType: IncomeInputType) => {
    setValues({ incomeInputType });
  };
  
  const setHoursPerWeek = (hoursPerWeek: number) => {
    setValues({ hoursPerWeek });
  };

  const saveScenario = (name = "Scenario") => {
    const scenarioName = `${name} ${savedScenarios.length + 1}`;
    const newScenario: SavedScenario = {
      id: nanoid(),
      name: scenarioName,
      values: { ...values },
      results: { ...results },
      timestamp: Date.now(),
    };
    
    setSavedScenarios((prev) => [...prev, newScenario]);
    setActiveScenarioId(newScenario.id);
  };

  const loadScenario = (id: string) => {
    const scenario = savedScenarios.find((s) => s.id === id);
    if (scenario) {
      setValuesState(scenario.values);
      setActiveScenarioId(id);
      setHasModified(false); // Reset modified flag when loading a scenario
    }
  };

  const deleteScenario = (id: string) => {
    setSavedScenarios((prev) => prev.filter((s) => s.id !== id));
    if (activeScenarioId === id) {
      setActiveScenarioId(null);
    }
  };

  const compareScenarios = (ids: string[]) => {
    // Future implementation: add comparison functionality
    console.log("Comparing scenarios:", ids);
  };
  
  const resetToDefaults = () => {
    setValuesState(DEFAULT_CALCULATOR_VALUES as CalculatorValues);
    setActiveScenarioId(null);
    setHasModified(false);
  };

  const contextValue: CalculatorContextType = {
    values,
    results,
    savedScenarios,
    activeScenarioId,
    hasModified,
    setValues,
    setFrequency,
    setEmploymentType,
    setTaxYear,
    setIncomeInputType,
    setHoursPerWeek,
    saveScenario,
    loadScenario,
    deleteScenario,
    compareScenarios,
    resetToDefaults,
  };

  return (
    <CalculatorContext.Provider value={contextValue}>
      {children}
    </CalculatorContext.Provider>
  );
};

// For Fast Refresh compatibility, we need to use the named export pattern recommended by React team
export const useCalculator = () => {
  const context = useContext(CalculatorContext);
  if (context === undefined) {
    throw new Error("useCalculator must be used within a CalculatorProvider");
  }
  return context;
}
